// Links in a sheet's cells (Fixes 9): a cell whose text is a web or mail address opens it, =HYPERLINK(url, label)
// shows its label over the address, and a cell can carry a link of its own (Ctrl or Cmd K, the right-click menu,
// write_sheet's links), kept in its look as ln. Only web and mail addresses are ever a link: a javascript: or data:
// address is text, so a cell an agent or a feed wrote never runs anything when it is clicked.

import type { CellFmt } from "./format";
import { isErr, type Scalar } from "./values";

/** The longest address a cell's link keeps. */
export const MAX_LINK = 2000;

const WEB = /^https?:\/\/[^\s/?#]+\.[^\s/?#]+[^\s]*$|^https?:\/\/localhost(:\d+)?([/?#][^\s]*)?$/i;
const MAIL = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const EMAIL = /^[^\s@:/]+@[^\s@:/]+\.[a-z]{2,}$/i;
const WWW = /^www\.[^\s/?#]+\.[^\s/?#]+[^\s]*$/i;

/** An address as a link opens it, or null: http and https, mailto, and www. or a bare email made into one. */
export function linkOf(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s || s.length > MAX_LINK || /[\s<>"`]/.test(s)) return null;
  if (WEB.test(s)) return validUrl(s) ? s : null;
  if (MAIL.test(s)) return s;
  if (WWW.test(s)) return validUrl(`https://${s}`) ? `https://${s}` : null;
  if (EMAIL.test(s)) return `mailto:${s}`;
  return null;
}

/** What a person types in the link box, as an address: a bare name gets https:// as a browser does. */
export function typedLink(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const direct = linkOf(s);
  if (direct) return direct;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  return linkOf(`https://${s}`);
}

function validUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.hostname;
  } catch {
    return false;
  }
}

/** The address a value is when the whole of it is one (a cell's text, or what a formula gave), else null. */
export function autoLink(v: Scalar): string | null {
  if (typeof v !== "string" || isErr(v) || v.length > MAX_LINK) return null;
  return linkOf(v);
}

/** A cell's link: its own (ln), else a HYPERLINK formula's, else the address its value is. */
export function cellLink(own: Pick<CellFmt, "ln"> | null | undefined, formulaLink: string | null | undefined, v: Scalar): string | null {
  return own?.ln ?? formulaLink ?? autoLink(v);
}

/** An address shortened for a card: its host and the start of its path, without https:// or www. */
export function shortLink(url: string, max = 48): string {
  const s = url.replace(/^mailto:/i, "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** A label and an address as a HYPERLINK formula, for a CSV that keeps the link (each quote doubled). */
export function hyperlinkFormula(url: string, label: string): string {
  return `=HYPERLINK("${url.replace(/"/g, '""')}","${label.replace(/"/g, '""')}")`;
}

/**
 * A CSV's or a paste's cell that is just a HYPERLINK of a web or mail address and a label in quotes, as a sheet
 * writes one (hyperlinkFormula): kept as the formula. Any other text starting with = stays text.
 */
export function plainHyperlink(raw: string): boolean {
  const m = /^=HYPERLINK\(\s*"((?:[^"]|"")*)"\s*(?:,\s*"((?:[^"]|"")*)"\s*)?\)$/i.exec(raw.trim());
  return !!m && !!linkOf(m[1].replace(/""/g, '"'));
}
